import { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';

import {
  IRequirementsMandatoryNew,
  IStructuresUnit,
} from '../../../../APIPackage/RequirementsNpaModule/interfaces/IStructuresUnitsAsTreeResponse';
import { IconCollapse } from '../../../../UIPackage/IconsModule/components/IconCollapse';
import styles from './StructuresUnitItem.module.scss';
import { Checkbox } from '../../../../UIPackage/MainModule/components/Checkbox';
import { InfoBlock } from '../../../../CorePackage/MainModule/components/InfoBlock';
import { fetchMandatoryRequirementsListNew } from '../../../../APIPackage/RequirementsNpaModule/services/requirementsNpaApi';

interface IStructuresUnitItemProps {
  unit: IStructuresUnit;
  npaId: number;
  level?: number;
  selectedItems: { [suId: string]: IRequirementsMandatoryNew[] };
  setSelectedItems: (value: { [suId: string]: IRequirementsMandatoryNew[] }) => void;
  withMandatory: boolean;
}

const getIsChecked = (
  unit: IStructuresUnit,
  selectedItems: { [suId: string]: IRequirementsMandatoryNew[] },
): boolean => {
  if (unit.children.length) {
    return unit.children.some(item => getIsChecked(item, selectedItems));
  }

  return !!unit.id && !!selectedItems[unit.id];
};

export const StructuresUnitItem = ({
  unit,
  npaId,
  level = 0,
  selectedItems,
  setSelectedItems,
  withMandatory,
}: IStructuresUnitItemProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const isUnitSelectable = useMemo(() => !withMandatory && !unit.children.length, [unit, withMandatory]);
  const isUnitChecked = useMemo(() => getIsChecked(unit, selectedItems), [unit, selectedItems]);
  const [mandatoryList, setMandatoryList] = useState(unit.data);

  const handleClickOnUnit = useCallback(() => {
    if (!isUnitSelectable) {
      return;
    }

    let value: { [suId: string]: IRequirementsMandatoryNew[] };

    if (isUnitChecked) {
      value = { ...selectedItems };
      delete value[unit.id || ''];
    } else {
      value = { ...selectedItems, [unit.id || '']: [] };
    }

    setSelectedItems(value);
  }, [isUnitSelectable, isUnitChecked, unit, selectedItems, setSelectedItems]);

  const handleClickOnMandatory = useCallback(
    (mandatory: IRequirementsMandatoryNew) => {
      const isMandatoryChecked =
        !!unit.id && !!selectedItems[unit.id]?.some(selectedItem => selectedItem.id === mandatory.id);

      let value: { [suId: string]: IRequirementsMandatoryNew[] };

      if (isMandatoryChecked) {
        value = {
          ...selectedItems,
          [unit.id || '']: selectedItems[unit.id || ''].filter(item => item.id !== mandatory.id),
        };

        if (!value[unit.id || ''].length) delete value[unit.id || ''];
      } else {
        value = {
          ...selectedItems,
          [unit.id || '']: [...(selectedItems[unit.id || ''] || []), mandatory],
        };
      }

      setSelectedItems(value);
    },
    [unit, selectedItems, setSelectedItems],
  );

  useEffect(() => {
    if (isOpen && withMandatory && (!mandatoryList || mandatoryList.length !== unit.dataSize)) {
      unit.id &&
        fetchMandatoryRequirementsListNew({ npaId, suId: unit.id }).then(value => {
          setMandatoryList(value);
        });
    }
  }, [npaId, unit, isOpen, withMandatory, mandatoryList, setMandatoryList]);

  return (
    <div>
      <div
        className={classNames(styles.Unit, { [styles.SelectableUnit]: isUnitSelectable })}
        style={{ paddingLeft: `${level * 25}px` }}
        onClick={handleClickOnUnit}
      >
        {unit.children.length || (withMandatory && unit.id) ? (
          <div onClick={() => setIsOpen(!isOpen)}>
            <IconCollapse className={`${styles.IconCollapse} ${isOpen && styles.IconCollapseOpen}`} />
          </div>
        ) : (
          <div style={{ width: 15 }} />
        )}
        <Checkbox theme="system" checked={isUnitChecked} />
        {unit.levelTitle}: {unit.value}
      </div>
      {isOpen && (
        <div>
          {unit.children.map((item, index) => (
            <StructuresUnitItem
              key={index}
              npaId={npaId}
              unit={item}
              level={level + 1}
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              withMandatory={withMandatory}
            />
          ))}
        </div>
      )}
      {withMandatory && isOpen && !!unit.id && (
        <div style={{ paddingLeft: `${level * 25}px` }}>
          <span className={styles.MandatoryHelpText}>Формулировки:</span>
          {!unit.dataSize && (
            <InfoBlock marginBottom="8px">
              Для этой структурной единицы отсутствуют формулировки обязательных требований
            </InfoBlock>
          )}
          {mandatoryList?.map(item => {
            const isMandatoryChecked =
              !!unit.id && !!selectedItems[unit.id]?.some(selectedItem => selectedItem.id === item.id);

            return (
              <div key={item.id} className={styles.MandatoryItem} onClick={() => handleClickOnMandatory(item)}>
                <Checkbox theme="system" checked={isMandatoryChecked} />
                <span>{item.title}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
